import { motion } from "framer-motion";

import type { AgentRun, AgentRunStatus } from "@/lib/api";
import { LEDDot } from "@/components/ui/LEDDot";
import { cn, formatRelativeTime } from "@/lib/formatters";

interface RunsListProps {
  runs: AgentRun[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const STATUS_LED: Record<AgentRunStatus, "ok" | "warn" | "danger" | "info" | "off"> = {
  queued: "warn",
  running: "info",
  success: "ok",
  failure: "danger",
  cancelled: "off",
};

const MODE_LABEL: Record<string, string> = {
  seo_fixer: "fixer",
  content_autopilot: "autopilot",
  plan: "plan",
};

const MODE_HUE: Record<string, string> = {
  seo_fixer: "text-magenta border-magenta/40 bg-magenta/5",
  content_autopilot: "text-violet border-violet/40 bg-violet/5",
  plan: "text-primary border-primary/40 bg-primary/5",
};

export function RunsList({ runs, selectedId, onSelect }: RunsListProps) {
  if (runs.length === 0) {
    return (
      <div className="surface p-6 text-center">
        <p className="font-mono text-sm text-muted">$ no agent runs yet</p>
      </div>
    );
  }

  return (
    <div className="surface overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <p className="path-chip">runs</p>
        <span className="font-mono text-xs text-muted">{runs.length}</span>
      </div>
      <ul className="divide-y divide-border/40 max-h-[70vh] overflow-y-auto">
        {runs.map((run, i) => {
          const selected = run.id === selectedId;
          return (
            <motion.li
              key={run.id}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.3) }}
            >
              <button
                type="button"
                onClick={() => onSelect(run.id)}
                className={cn(
                  "w-full text-left px-4 py-3 transition-colors",
                  selected
                    ? "bg-gradient-to-r from-primary/10 to-transparent border-l-2 border-primary"
                    : "border-l-2 border-transparent hover:bg-violet/[0.04] hover:border-violet/40",
                )}
              >
                <div className="flex items-center gap-2">
                  <LEDDot state={STATUS_LED[run.status]} size={6} pulse={run.status === "running"} />
                  {run.mode ? (
                    <span
                      className={cn(
                        "px-1.5 py-0.5 rounded-sm font-mono text-[10px] uppercase border",
                        MODE_HUE[run.mode] ?? "text-muted border-border",
                      )}
                    >
                      {MODE_LABEL[run.mode] ?? run.mode}
                    </span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded-sm font-mono text-[10px] uppercase border text-muted border-border">
                      {run.agent}
                    </span>
                  )}
                  <span className="font-mono text-[11px] text-muted ml-auto">
                    {run.started_at ? formatRelativeTime(run.started_at) : "—"}
                  </span>
                </div>

                <p className="mt-1.5 text-sm text-text truncate">
                  {run.week || run.summary || "sin detalle"}
                  {run.agent_status && (
                    <span className="text-muted/70 font-mono text-xs"> · {run.agent_status}</span>
                  )}
                </p>

                <div className="mt-1 flex items-center gap-3 font-mono text-[10px] text-muted/70">
                  <span>{(run.edits_json ?? []).length} edits</span>
                  {run.pr_url && <span className="text-success">PR</span>}
                  {run.auto_merge && <span className="text-success">auto</span>}
                  {run.status === "failure" && <span className="text-danger">failed</span>}
                </div>
              </button>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}
